import { useCallback, useEffect, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  MenuBook as JournalIcon,
  PictureAsPdf as PdfIcon,
} from "@mui/icons-material";
import { PageContainer } from "@/features/ui";
import { PDFViewer } from "@react-pdf/renderer";

import { useAuth } from "@/features/authentication";
import { PermissionEnum } from "@/features/permissions";
import {
  SalesPaymentPdf,
  GeneralInformationSection,
  SalesInvoicePaymentsSection,
} from "@/features/sales-payments";
import { apiOutletClient, getErrorMessage } from "@/features/shared";
import {
  CenteredLoading,
  JournalDetailsDialog,
  ResponsiveToolbarButton,
} from "@/features/ui";

export const SalesPayment = () => {
  const { referenceNumber } = useParams();
  const navigate = useNavigate();
  const { outlet, user, hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [salesPayment, setSalesPayment] = useState(null);
  const [error, setError] = useState(null);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [journalDialogOpen, setJournalDialogOpen] = useState(false);

  const loadSalesPayment = useCallback(async () => {
    if (!outlet) return;

    setLoading(true);
    setError(null);
    try {
      const {
        data: { data },
      } = await apiOutletClient.get(`sales-payments/${referenceNumber}`, {
        params: {
          withBuyer: true,
          withSeller: true,
          withCashBankOutletChartOfAccount: true,
          withSalesInvoicePayments: true,
          withCreator: true,
        },
      });
      setSalesPayment(data);
    } catch (error) {
      setError(
        getErrorMessage(error, "Gagal memuat pembayaran penjualan")
      );
    } finally {
      setLoading(false);
    }
  }, [outlet, referenceNumber]);

  useEffect(() => {
    loadSalesPayment();
  }, [loadSalesPayment]);

  if (loading) {
    return <CenteredLoading sx={{ height: "100vh" }} />;
  }

  if (error) {
    return (
      <Typography variant="h6" color="error">
        {error}
      </Typography>
    );
  }

  const toolbarContent = (
    <>
      {hasPermission(PermissionEnum.UPDATE_SALES_PAYMENTS) && (
        <ResponsiveToolbarButton
          label="Ubah"
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => navigate("edit")}
        />
      )}
      <ResponsiveToolbarButton
        label="Rincian Jurnal"
        variant="outlined"
        startIcon={<JournalIcon />}
        onClick={() => setJournalDialogOpen(true)}
      />
      <ResponsiveToolbarButton
        label="Cetak PDF"
        variant="outlined"
        startIcon={<PdfIcon />}
        onClick={() => setPdfDialogOpen(true)}
      />
    </>
  );

  return (
    <PageContainer
      maxWidth="xl"
      title="Penerimaan Penjualan"
      breadcrumbs={[
        { title: "Penerimaan Penjualan", path: "/sales/sales-payments" },
        { title: referenceNumber },
      ]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
    >
      <Stack spacing={2}>
        <GeneralInformationSection salesPayment={salesPayment} />
        <SalesInvoicePaymentsSection
          salesInvoicePayments={salesPayment.salesInvoicePayments}
        />

        {/* PDF Dialog */}
        <Dialog
          open={pdfDialogOpen}
          onClose={() => setPdfDialogOpen(false)}
          maxWidth="lg"
          fullWidth
          slotProps={{
            paper: {
              sx: { height: "90vh" },
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Penerimaan Penjualan - {referenceNumber}
            <IconButton onClick={() => setPdfDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, height: "100%" }}>
            {salesPayment && user && (
              <PDFViewer width="100%" height="100%">
                <SalesPaymentPdf salesPayment={salesPayment} user={user} />
              </PDFViewer>
            )}
          </DialogContent>
        </Dialog>
        {/* Journal Details Dialog */}
        <JournalDetailsDialog
          open={journalDialogOpen}
          onClose={() => setJournalDialogOpen(false)}
          transactionableType="SALES_PAYMENT"
          transactionableId={salesPayment?.id}
        />
      </Stack>
    </PageContainer>
  );
};
